import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Switch,
  Tooltip,
  Button,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import CustomFrameLayoutPreview from '../shared/CustomFrameLayoutPreview';
import { userCardEnterSx } from '../shared/UserPageShell';

export type AdminCustomFrameChannel = 'pro' | 'lite';

/** admin = admin cấp quyền; user = user bật trong Pro/Lite */
export type CustomFramesVariant = 'admin' | 'user';

interface CustomFramesProps {
  userId: string;
  channel?: AdminCustomFrameChannel;
  variant?: CustomFramesVariant;
  embedded?: boolean;
}

const FRAME_SIZE_PRIORITY = ['2x4', '4x2', '2x6', '6x2', '4x6', '6x4', '3x4', '4x3'];

const normalizeFrameSize = (sizeValue?: string) =>
  String(sizeValue || '').trim().toLowerCase();

const CHANNEL_COPY: Record<
  AdminCustomFrameChannel,
  { title: string; description: string }
> = {
  pro: {
    title: 'Custom Frames — M-Photo Pro',
    description:
      'Hiện toàn bộ khung. Bật khung nào thì tài khoản này thấy khung đó ở M-Photo Pro. Không ảnh hưởng M-Photo Lite.',
  },
  lite: {
    title: 'Custom Frames — M-Photo Lite',
    description:
      'Hiện toàn bộ khung. Bật khung nào thì tài khoản này thấy khung đó ở M-Photo Lite. Không ảnh hưởng M-Photo Pro.',
  },
};

const CustomFrames: React.FC<CustomFramesProps> = ({
  userId,
  channel = 'pro',
  variant = 'admin',
  embedded = false,
}) => {
  const navigate = useNavigate();
  const [customFrames, setCustomFrames] = useState<api.CustomFrame[]>([]);
  const [enabledFrameIds, setEnabledFrameIds] = useState<Set<string>>(new Set());
  const [selectedFrameSizes, setSelectedFrameSizes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [togglingFrameId, setTogglingFrameId] = useState<string | null>(null);

  const isAdminMode = variant === 'admin';
  const isUserMode = variant === 'user';

  const assetsPath = useCallback(
    (frameId: string) =>
      channel === 'lite'
        ? `/users/${userId}/m-photo-lite/custom-frames/${frameId}`
        : `/users/${userId}/custom-frames/${frameId}`,
    [channel, userId]
  );

  const fetchData = useCallback(async () => {
    if (!userId) return;

    try {
      setIsLoading(true);

      const settings = await api.getUserCustomFrameSettings(userId);
      setCustomFrames(settings.frames || []);
      const enabledIds = isAdminMode
        ? settings.grantedFrameIds
        : channel === 'pro'
          ? settings.enabledProIds
          : settings.enabledLiteIds;
      setEnabledFrameIds(new Set((enabledIds || []).map((id) => String(id))));
    } catch (error) {
      console.error('Error fetching custom frames:', error);
      toast.error('Không thể tải danh sách custom frames');
    } finally {
      setIsLoading(false);
    }
  }, [userId, channel, isAdminMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async (frameId: string, currentEnabled: boolean) => {
    try {
      setTogglingFrameId(frameId);
      const newEnabled = !currentEnabled;

      if (isAdminMode) {
        await api.toggleAdminCustomFrameGrant(userId, frameId, newEnabled);
      } else {
        const toggle =
          channel === 'pro' ? api.toggleCustomFrameEnablePro : api.toggleCustomFrameEnableLite;
        await toggle(userId, frameId, newEnabled);
      }

      setEnabledFrameIds((prev) => {
        const newSet = new Set(prev);
        if (newEnabled) {
          newSet.add(frameId);
        } else {
          newSet.delete(frameId);
        }
        return newSet;
      });

      toast.success(newEnabled ? 'Đã bật' : 'Đã tắt');
    } catch (error: any) {
      console.error('Error toggling custom frame:', error);
      toast.error(error.response?.data?.message || 'Không thể cập nhật trạng thái custom frame');
    } finally {
      setTogglingFrameId(null);
    }
  };

  const orderedSizes = useMemo(() => {
    const availableSizes = new Set(
      customFrames.map((frame) => normalizeFrameSize(frame.size)).filter(Boolean),
    );
    const orderedByPriority = FRAME_SIZE_PRIORITY.map((size) => normalizeFrameSize(size));
    const remainingSizes = Array.from(availableSizes)
      .filter((size) => !orderedByPriority.includes(size))
      .sort((a, b) => a.localeCompare(b));
    return [...orderedByPriority, ...remainingSizes];
  }, [customFrames]);

  const sortedFrames = useMemo(() => {
    const priorityMap = new Map(
      FRAME_SIZE_PRIORITY.map((size, index) => [normalizeFrameSize(size), index]),
    );
    return [...customFrames].sort((a, b) => {
      const aSize = normalizeFrameSize(a.size);
      const bSize = normalizeFrameSize(b.size);
      const aPriority = priorityMap.has(aSize) ? priorityMap.get(aSize)! : Number.MAX_SAFE_INTEGER;
      const bPriority = priorityMap.has(bSize) ? priorityMap.get(bSize)! : Number.MAX_SAFE_INTEGER;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [customFrames]);

  const visibleFrames = useMemo(() => {
    if (selectedFrameSizes.length === 0) return sortedFrames;
    const selectedSet = new Set(selectedFrameSizes.map((size) => normalizeFrameSize(size)));
    return sortedFrames.filter((frame) => selectedSet.has(normalizeFrameSize(frame.size)));
  }, [sortedFrames, selectedFrameSizes]);

  const toggleFrameSizeFilter = (size: string) => {
    setSelectedFrameSizes((prev) =>
      prev.includes(size) ? prev.filter((item) => item !== size) : [...prev, size],
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const adminCopy = {
    title: 'Custom Frames — Admin',
    description:
      'Cấp quyền khung cho user (lớp trên). Chỉ khung được cấp mới xuất hiện trong M-Photo Pro / Lite để user bật.',
  };

  const copy = isAdminMode ? adminCopy : CHANNEL_COPY[channel];
  const showToggles = isAdminMode || isUserMode;

  const content = (
    <>
      {!embedded && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            {copy.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.description}
          </Typography>
        </Paper>
      )}

      {isUserMode && customFrames.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Chưa có custom frame nào trong hệ thống.
        </Alert>
      )}

      {customFrames.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: alpha('#ff8ba7', 0.28),
            background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha('#ff8ba7', 0.06)} 100%)`,
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 1, color: '#e86a8a', fontSize: { xs: '0.95rem', sm: '1rem' } }}
          >
            Lọc theo kích thước khung
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(5, minmax(0, 1fr))',
                sm: 'repeat(6, minmax(0, 1fr))',
                md: 'repeat(8, minmax(0, 1fr))',
              },
              gap: { xs: 0.6, sm: 1 },
            }}
          >
            <Button
              size="small"
              variant={selectedFrameSizes.length === 0 ? 'contained' : 'outlined'}
              onClick={() => setSelectedFrameSizes([])}
              sx={{
                minWidth: 0,
                px: { xs: 0.25, sm: 1 },
                fontSize: { xs: '0.68rem', sm: '0.8125rem' },
                whiteSpace: 'nowrap',
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 700,
                ...(selectedFrameSizes.length === 0
                  ? {
                      bgcolor: '#e86a8a',
                      color: '#fff',
                      boxShadow: `0 2px 8px ${alpha('#e86a8a', 0.3)}`,
                      '&:hover': { bgcolor: alpha('#e86a8a', 0.88) },
                    }
                  : {
                      color: '#e86a8a',
                      borderColor: alpha('#ff8ba7', 0.45),
                      bgcolor: alpha('#fff', 0.8),
                      '&:hover': {
                        borderColor: '#e86a8a',
                        bgcolor: alpha('#ff8ba7', 0.12),
                      },
                    }),
              }}
            >
              Tất cả
            </Button>
            {orderedSizes.map((size) => {
              const selected = selectedFrameSizes.includes(size);
              return (
                <Button
                  key={size}
                  size="small"
                  variant={selected ? 'contained' : 'outlined'}
                  onClick={() => toggleFrameSizeFilter(size)}
                  sx={{
                    minWidth: 0,
                    px: { xs: 0.25, sm: 1 },
                    fontSize: { xs: '0.68rem', sm: '0.8125rem' },
                    whiteSpace: 'nowrap',
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    ...(selected
                      ? {
                          bgcolor: '#e86a8a',
                          color: '#fff',
                          boxShadow: `0 2px 8px ${alpha('#e86a8a', 0.3)}`,
                          '&:hover': { bgcolor: alpha('#e86a8a', 0.88) },
                        }
                      : {
                          color: '#e86a8a',
                          borderColor: alpha('#ff8ba7', 0.45),
                          bgcolor: alpha('#fff', 0.8),
                          '&:hover': {
                            borderColor: '#e86a8a',
                            bgcolor: alpha('#ff8ba7', 0.12),
                          },
                        }),
                  }}
                >
                  {size}
                </Button>
              );
            })}
          </Box>
        </Paper>
      )}

      {customFrames.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            Không có custom frames nào trong hệ thống
          </Typography>
        </Paper>
      ) : visibleFrames.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            Không có frame trong bộ lọc hiện tại
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 2, md: 3 }}>
          {(() => {
            let lastSizeKey = '';
            return visibleFrames.map((frame, index) => {
              const isEnabled = enabledFrameIds.has(frame._id);
              const isToggling = togglingFrameId === frame._id;
              const sizeKey = normalizeFrameSize(frame.size) || 'other';
              const showSizeTitle = sizeKey !== lastSizeKey;
              if (showSizeTitle) lastSizeKey = sizeKey;

              return (
                <React.Fragment key={frame._id}>
                  {showSizeTitle && (
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, mb: 0.25, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {(frame.size || 'Other').toUpperCase()} frames
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6} sm={6} md={4} sx={{ minWidth: 0 }}>
                    <Paper
                      elevation={0}
                      onClick={() => navigate(assetsPath(frame._id))}
                      sx={{
                        p: { xs: 1.25, sm: 2 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: isEnabled ? alpha('#4caf50', 0.7) : alpha('#ff8ba7', 0.28),
                        boxShadow: isEnabled
                          ? `0 4px 14px ${alpha('#4caf50', 0.15)}`
                          : `0 4px 14px ${alpha('#ff8ba7', 0.08)}`,
                        background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
                        cursor: 'pointer',
                        minWidth: 0,
                        overflow: 'visible',
                        ...userCardEnterSx(index),
                        '&:hover': {
                          boxShadow: isEnabled
                            ? `0 6px 18px ${alpha('#4caf50', 0.2)}`
                            : `0 6px 18px ${alpha('#ff8ba7', 0.16)}`,
                        },
                      }}
                    >
                      {showToggles && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
                          <Tooltip
                            title={
                              isAdminMode
                                ? isEnabled
                                  ? 'Admin đã cấp — Click thu hồi'
                                  : 'Chưa cấp — Click cho phép user dùng'
                                : isEnabled
                                  ? 'Đang bật cho tài khoản này — Click tắt'
                                  : 'Đang tắt — Click bật cho tài khoản này'
                            }
                          >
                            <Switch
                              checked={isEnabled}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleToggle(frame._id, isEnabled)}
                              disabled={isToggling}
                              size="small"
                              color="primary"
                            />
                          </Tooltip>
                        </Box>
                      )}

                      <CustomFrameLayoutPreview
                        width={frame.width}
                        height={frame.height}
                        size={frame.size}
                        photoPositions={frame.photoPositions}
                        maxHeight={{ xs: 180, sm: 220 }}
                      />

                      <Box sx={{ mt: 'auto' }}>
                        <Typography
                          fontWeight={700}
                          sx={{
                            color: '#e86a8a',
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mt: 0.75,
                          }}
                        >
                          {frame.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: alpha('#e86a8a', 0.7),
                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                          }}
                        >
                          {frame.size} · {frame.maxPhotos} ảnh
                        </Typography>
                        <Button
                          size="small"
                          sx={{
                            mt: 0.75,
                            textTransform: 'none',
                            fontWeight: 600,
                            color: '#e86a8a',
                            fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                            px: { xs: 0.5, sm: 1 },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(assetsPath(frame._id));
                          }}
                        >
                          Xem user frames
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </React.Fragment>
              );
            });
          })()}
        </Grid>
      )}
    </>
  );

  if (embedded) return <Box className="user-page-enter">{content}</Box>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {content}
    </Container>
  );
};

export default CustomFrames;
